'use client';

import {useEffect, useRef, useState} from 'react';
import {ChevronDown, X} from 'lucide-react';
import {cn} from 'common/utils';

export type DateRange = '90d' | '30d' | 'all';

const DATE_RANGE_LABEL: Record<DateRange, string> = {
  '90d': 'Last 90 days',
  '30d': 'Last 30 days',
  all: 'All time',
};

// Exported for reuse by other quick-filter dropdowns that want this exact chip interaction
// (click to open, click an option to select, "All {label}" reset row) — e.g.
// company_list.tsx's industry filter on /companies/all.
export function FilterChip({
  label,
  displayValue,
  options,
  formatOption,
  onSelect,
  onClear,
}: {
  label: string;
  displayValue: string | null;
  options: string[];
  formatOption: (v: string) => string;
  onSelect: (v: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = displayValue !== null;

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div ref={ref} className='relative'>
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors',
          selected
            ? 'border-iq-orange bg-orange-50 text-iq-orange'
            : 'border-gray-200 text-gray-700 hover:bg-gray-50',
        )}>
        {displayValue ?? label}
        {selected ? (
          <X
            className='h-3.5 w-3.5 hover:opacity-70'
            onClick={(e) => {
              e.stopPropagation();
              onClear();
              setOpen(false);
            }}
          />
        ) : (
          <ChevronDown className='h-3.5 w-3.5' />
        )}
      </button>
      {open && (
        <div className='absolute z-20 mt-2 max-h-72 min-w-[180px] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg'>
          {displayValue && (
            <button
              onClick={() => {
                onClear();
                setOpen(false);
              }}
              className='w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50'>
              All {label.toLowerCase()}
            </button>
          )}
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => {
                onSelect(opt);
                setOpen(false);
              }}
              className='w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50'>
              {formatOption(opt)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Quick filters that narrow the feed in place — distinct from search_bar.tsx, which
// navigates away to a company's guide. "All experiences" and any chip with a selected value
// share the same light-orange active styling; selected chips also get an inline X to clear
// just that filter, plus a "Clear all filters" link once anything is active.
export function FilterBar({
  companies,
  roles,
  industries,
  company,
  role,
  industry,
  dateRange,
  isFiltered,
  onCompanyChange,
  onRoleChange,
  onIndustryChange,
  onDateRangeChange,
  onReset,
}: {
  companies: string[];
  roles: string[];
  // Omit entirely to hide the Industry chip — the real /companies feed has no
  // per-experience industry data (that's real only on /companies/all, plan §
  // Workstream 1 item 1g).
  industries?: string[];
  company: string | null;
  role: string | null;
  industry?: string | null;
  dateRange: DateRange;
  isFiltered: boolean;
  onCompanyChange: (v: string | null) => void;
  onRoleChange: (v: string | null) => void;
  onIndustryChange?: (v: string | null) => void;
  onDateRangeChange: (v: DateRange) => void;
  onReset: () => void;
}) {
  return (
    <div className='flex flex-wrap items-center gap-2'>
      <button
        onClick={onReset}
        className={cn(
          'rounded-full border px-4 py-2 text-sm font-medium transition-colors',
          !isFiltered
            ? 'border-iq-orange bg-orange-50 text-iq-orange'
            : 'border-gray-200 text-gray-700 hover:bg-gray-50',
        )}>
        All experiences
      </button>
      <FilterChip
        label='Company'
        displayValue={company}
        options={companies}
        formatOption={(v) => v}
        onSelect={(v) => onCompanyChange(v)}
        onClear={() => onCompanyChange(null)}
      />
      <FilterChip
        label='Role'
        displayValue={role}
        options={roles}
        formatOption={(v) => v}
        onSelect={(v) => onRoleChange(v)}
        onClear={() => onRoleChange(null)}
      />
      {industries && onIndustryChange && (
        <FilterChip
          label='Industry'
          displayValue={industry ?? null}
          options={industries}
          formatOption={(v) => v}
          onSelect={(v) => onIndustryChange(v)}
          onClear={() => onIndustryChange(null)}
        />
      )}
      <FilterChip
        label='Last 90 days'
        displayValue={dateRange === '90d' ? null : DATE_RANGE_LABEL[dateRange]}
        options={['90d', '30d', 'all']}
        formatOption={(v) => DATE_RANGE_LABEL[v as DateRange]}
        onSelect={(v) => onDateRangeChange(v as DateRange)}
        onClear={() => onDateRangeChange('90d')}
      />
      {isFiltered && (
        <button
          onClick={onReset}
          className='text-sm font-medium text-gray-500 transition-colors hover:text-gray-700 hover:underline'>
          Clear all filters
        </button>
      )}
    </div>
  );
}
